import { useEffect, useMemo, useState } from 'react'

import { announceOperation } from '@/hooks/useOperations'
import { enqueueOperation } from '@/lib/api/client/operations'
import { getStorefrontSettings, previewStorefrontBatch } from '@/lib/api/client/storefronts'
import type { StorefrontBatchSelection } from '@/lib/api/client/storefronts'
import type { InventoryFilters } from '@/lib/api/client/inventory'
import type { EnqueueableOperationKind } from '@/lib/api/schemas/operations.schema'
import type { BatchItemPreview, StorefrontBatchPreview, StorefrontSettings, StorefrontTarget } from '@/lib/api/schemas/storefronts.schema'
import type { SelectionScope } from '@/lib/views/actions'
import { StorefrontModal } from '@/components/storefronts/StorefrontModal'

const TARGET_LABELS: Record<StorefrontTarget, string> = {
  wildberries: 'Wildberries',
  ymarket: 'Яндекс Маркет',
  ozon: 'Ozon',
  ori_site: 'ori',
}

const TARGETS: readonly StorefrontTarget[] = ['wildberries', 'ymarket', 'ozon', 'ori_site']
const BLOCKING_CODES = new Set([
  'not_in_stock',
  'stock_unassessed',
  'no_article',
  'no_uin',
  'no_verified_size',
  'no_photo',
  'product_content_not_configured',
  'provider_account_not_configured',
  'no_description',
  'no_weight',
  'no_category_mapping',
  'no_price',
  'seller_article_conflict',
  'publication_already_exists',
  'provider_validation',
  'provider_transport',
  'provider_mutation',
  'target_transport_failed',
  'target_validation_failed',
  'target_mapping_not_configured',
  'target_credentials_missing',
  'target_not_configured',
  'product_not_found',
  'media_probe_unavailable',
  'media_download_failed',
  'media_download_pending',
  'wildberries_photo_dimensions',
  'wildberries_photo_format',
  'wildberries_photo_size',
  'wildberries_photo_limit',
  'wildberries_video_limit',
  'wildberries_video_unavailable',
  'wildberries_publication_blocked',
  'barcode_missing',
])

const OPERATION_KINDS: Partial<Record<StorefrontTarget, EnqueueableOperationKind>> = {
  wildberries: 'wildberries_publish',
  ymarket: 'ymarket_publish',
  ozon: 'ozon_publish',
}

function itemKey(item: Pick<BatchItemPreview, 'productId' | 'target'>): string {
  return `${item.productId}:${item.target}`
}

function selectionCount(selection: SelectionScope<InventoryFilters>): number {
  return selection.kind === 'ids' ? selection.ids.length : selection.expectedTotal
}

function sourceLabel(source: string | null): string | null {
  if (source === 'uin' || source === 'forge_product_uin') return 'УИН'
  if (source === 'article_size') return 'Артикул + размер'
  if (source === 'sync_identifier' || source === 'forge_sync_identifier') return 'служебный идентификатор'
  return source
}

function isBlocking(code: string): boolean {
  return BLOCKING_CODES.has(code)
}

function targetReason(target: StorefrontTarget, settings: StorefrontSettings | null): string | null {
  if (target === 'ori_site') return 'Не подключено'
  if (settings === null) return 'Проверяем настройки…'
  const targetSettings = settings.targets[target]
  if (targetSettings.cardPublishingEnabled === false) return `Заливка на ${TARGET_LABELS[target]} выключена — включите в «Настройки карточек»`
  if (targetSettings.credentialStatus.state === 'missing') return 'Не подключено'
  if (OPERATION_KINDS[target] === undefined) return 'Заливка через очередь операций пока не поддерживается'
  return null
}

function targetEnabled(target: StorefrontTarget, settings: StorefrontSettings | null): boolean {
  return targetReason(target, settings) === null
}

function environmentLabel(target: StorefrontTarget, settings: StorefrontSettings | null): string | null {
  if (settings === null) return null
  return target === 'wildberries' && settings.targets[target].environment === 'sandbox'
    ? 'Песочница WB · реальные товары не затрагиваются'
    : 'Рабочий контур'
}

export function PublishToStorefrontDialog({
  selection,
  onDone,
  initialTarget = 'wildberries',
}: {
  selection: SelectionScope<InventoryFilters>
  onDone: () => void
  initialTarget?: StorefrontTarget
}) {
  const [target, setTarget] = useState<StorefrontTarget>(initialTarget)
  const [settings, setSettings] = useState<StorefrontSettings | null>(null)
  const [preview, setPreview] = useState<StorefrontBatchPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setBusy(true)
    getStorefrontSettings()
      .then((value) => { if (active) setSettings(value) })
      .catch((reason: unknown) => { if (active) setError(reason instanceof Error ? reason.message : 'Не удалось загрузить настройки карточек') })
      .finally(() => { if (active) setBusy(false) })
    return () => { active = false }
  }, [])

  useEffect(() => {
    if (settings === null || !targetEnabled(target, settings)) return undefined
    const controller = new AbortController()
    setPreview(null)
    setError(null)
    setBusy(true)
    const request: StorefrontBatchSelection = selection.kind === 'ids'
      ? { kind: 'ids', ids: selection.ids }
      : { kind: 'filter', filter: selection.filter, expectedTotal: selection.expectedTotal }
    previewStorefrontBatch(request, [target], controller.signal)
      .then((value) => { if (!controller.signal.aborted) setPreview(value) })
      .catch((reason: unknown) => { if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Не удалось подготовить публикацию') })
      .finally(() => { if (!controller.signal.aborted) setBusy(false) })
    return () => controller.abort()
  }, [selection, settings, target])

  const items = useMemo(() => preview?.items ?? [], [preview])
  const publishableItems = items.filter((item) => item.payload !== null && !item.refusals.some((refusal) => isBlocking(refusal.code)))
  const blockedItems = items.filter((item) => item.payload === null || item.refusals.some((refusal) => isBlocking(refusal.code)))
  const title = `Опубликовать на ${TARGET_LABELS[target]} · ${selectionCount(selection)} изделий`

  const confirm = async () => {
    const kind = OPERATION_KINDS[target]
    if (preview === null || publishableItems.length === 0 || busy || kind === undefined) return
    setBusy(true)
    setError(null)
    try {
      announceOperation(await enqueueOperation({ kind, params: { batchHash: preview.batchHash } }))
      onDone()
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Не удалось поставить заливку в очередь')
      setBusy(false)
    }
  }

  return (
    <StorefrontModal title={title} labelledBy="publish-to-storefront-title" wide onClose={onDone} footer={(
      <>
        <button type="button" className="btn btn-ghost" onClick={onDone}>Отмена</button>
        <button type="button" className="btn btn-primary" disabled={busy || preview === null || publishableItems.length === 0 || OPERATION_KINDS[target] === undefined} onClick={() => { void confirm() }}>
          Опубликовать {publishableItems.length}
        </button>
      </>
    )}>
      <div className="space-y-4">
        <fieldset>
          <legend className="text-sm font-semibold">Куда заливать</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {TARGETS.map((candidate) => {
              const reason = targetReason(candidate, settings)
              return (
                <label key={candidate} className={`rounded-lg border border-rule p-3 text-sm ${reason ? 'text-ink-muted' : 'cursor-pointer'}`}>
                  <span className="flex items-center gap-2">
                    <input type="radio" name="storefront-target" checked={target === candidate} disabled={reason !== null || busy} onChange={() => setTarget(candidate)} />
                    <span className="font-medium">{TARGET_LABELS[candidate]}</span>
                  </span>
                  {environmentLabel(candidate, settings) ? <span className={`mt-1 block text-xs ${candidate === 'wildberries' && settings?.targets[candidate].environment === 'sandbox' ? 'font-semibold text-st-unpaid' : 'text-ink-muted'}`} data-storefront-environment={settings?.targets[candidate].environment}>{environmentLabel(candidate, settings)}</span> : null}
                  {reason ? <span className="mt-1 block text-xs">{reason}</span> : null}
                </label>
              )
            })}
          </div>
        </fieldset>
        <p className="text-xs text-ink-muted">До подтверждения запрос поставщику не уходит. После подтверждения заливка встанет в очередь, а ход выполнения будет виден в окне «Операции».</p>
        {busy && preview === null ? <p role="status" className="text-sm text-ink-muted">Готовим точный список отправки…</p> : null}
        {error ? <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-st-unpaid">{error}</p> : null}
        {preview ? <>
          <p className="rounded-md bg-bone/60 px-3 py-2 text-sm font-semibold">Будет отправлено: {publishableItems.length} · Пропущено: {blockedItems.length}</p>
          <div className="space-y-3">
            {items.map((item) => {
              const blocking = item.refusals.filter((refusal) => isBlocking(refusal.code))
              const advisory = item.refusals.filter((refusal) => !isBlocking(refusal.code))
              return (
                <article key={itemKey(item)} className="rounded-lg border border-rule p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-semibold">{item.sellerArticle ?? 'Идентификатор не определён'}</h3>
                    <span className="text-xs text-ink-muted">{TARGET_LABELS[item.target]}</span>
                  </div>
                  <p className="mt-1 text-xs text-ink-2">Идентификатор в карточке: {item.sellerArticle ?? 'не определён'}{sourceLabel(item.sellerArticleSource) ? ` · ${sourceLabel(item.sellerArticleSource)}` : ''}</p>
                  <p className="mt-1 text-xs text-ink-2">Группа: {item.group ?? 'Без группы'} · Фото: {item.photoCount} · Видео: {item.videoCount}</p>
                  {blocking.length > 0 ? <ul className="mt-2 space-y-1 text-xs text-st-unpaid">{blocking.map((refusal) => <li key={refusal.code}>Не зальём: {refusal.label}</li>)}</ul> : null}
                  {advisory.length > 0 ? <ul className="mt-2 space-y-1 text-xs text-ink-2">{advisory.map((refusal) => <li key={refusal.code}>Внимание: {refusal.label}</li>)}</ul> : null}
                </article>
              )
            })}
          </div>
        </> : null}
      </div>
    </StorefrontModal>
  )
}
